import React, { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, Loader2, Check } from 'lucide-react';
import { useOrg } from '../../../hooks/useOrg';
import { api } from '../../../lib/api';

// Referral preferences — what the referral search tells attorneys (and
// patients) about this practice: lien acceptance and languages spoken.
// Until a practice answers, its search cards say "LOP unconfirmed", so
// filling this in is how a clinic starts winning referrals. Backed by the
// flag-gated referrals API: when the feature isn't enabled in this
// environment the card simply doesn't render.

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'pt', label: 'Português' },
  { code: 'ht', label: 'Kreyòl' },
  { code: 'zh', label: '中文' },
];

export default function ReferralPreferencesCard() {
  const { currentOrg } = useOrg();
  const [settings, setSettings] = useState(null); // null = loading/hidden
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!currentOrg?.id || currentOrg.type !== 'medical_practice') return;
    try {
      setSettings(await api.getReferralSettings(currentOrg.id));
    } catch {
      // Feature flag off (404) or transient failure — stay hidden.
      setSettings(null);
    }
  }, [currentOrg?.id, currentOrg?.type]);

  useEffect(() => { load(); }, [load]);

  if (currentOrg?.type !== 'medical_practice' || !settings) return null;

  const toggleLang = (code) => {
    setSaved(false);
    setSettings((s) => {
      const langs = s.languages || [];
      return { ...s, languages: langs.includes(code) ? langs.filter((l) => l !== code) : [...langs, code] };
    });
  };

  const save = async () => {
    setSaving(true); setError('');
    try {
      const updated = await api.updateReferralSettings(currentOrg.id, {
        accepts_lop: !!settings.accepts_lop,
        languages: (settings.languages || []).length ? settings.languages : ['en'],
        referral_notes: settings.referral_notes || null,
      });
      setSettings(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-elevated border border-theme rounded-xl p-5 mt-5">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck size={15} className="text-accent-light" />
        <h3 className="text-[15px] font-semibold text-primary-c">Referral preferences</h3>
      </div>
      <p className="text-[12.5px] text-muted-c mb-5 max-w-[560px]">
        This is what attorneys and patients see when they search for a provider. Until you
        answer, your practice shows as "LOP unconfirmed" in referral results.
      </p>

      {error && (
        <div className="px-4 py-3 rounded-[10px] bg-danger-soft text-danger text-[13px] font-medium mb-4">{error}</div>
      )}

      {/* Lien acceptance */}
      <button
        type="button"
        onClick={() => { setSaved(false); setSettings((s) => ({ ...s, accepts_lop: !s.accepts_lop })); }}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-[10px] border text-left transition-all mb-4 ${
          settings.accepts_lop ? 'bg-accent-glow border-strong' : 'bg-tertiary border-theme hover:bg-hover-surface'}`}
      >
        <span className={`w-9 h-9 rounded-[10px] flex items-center justify-center shrink-0 ${
          settings.accepts_lop ? 'gradient-accent text-white' : 'bg-elevated text-muted-c border border-theme'}`}>
          <ShieldCheck size={16} />
        </span>
        <span className="flex-1">
          <span className="block text-[13px] font-semibold text-primary-c">We treat on a letter of protection (LOP)</span>
          <span className="block text-[11.5px] text-muted-c">
            Shows a green "Accepts LOP" badge on your practice in referral search results.
          </span>
        </span>
        <span className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${
          settings.accepts_lop ? 'gradient-accent border-transparent text-white' : 'border-strong'}`}>
          {settings.accepts_lop && <Check size={12} strokeWidth={3} />}
        </span>
      </button>

      {/* Languages */}
      <div className="mb-4">
        <label className="block text-[11px] font-bold uppercase tracking-[1px] text-muted-c mb-2">
          Languages your team speaks
        </label>
        <div className="flex flex-wrap gap-1.5">
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => toggleLang(l.code)}
              className={`px-3 py-[7px] rounded-full text-[12px] font-semibold border transition-all ${
                (settings.languages || []).includes(l.code)
                  ? 'gradient-accent text-white border-transparent'
                  : 'bg-tertiary text-secondary-c border-theme hover:bg-hover-surface'}`}
            >
              {l.label}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-muted-c mt-1.5">
          Searches filtered to a language only include practices that list it.
        </p>
      </div>

      {/* Note */}
      <div className="mb-5">
        <label className="block text-[11px] font-bold uppercase tracking-[1px] text-muted-c mb-2">
          Note to referring firms <span className="font-normal normal-case tracking-normal">(optional)</span>
        </label>
        <input
          className="w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] focus:outline-none focus:border-[#0DA7CA] transition-all"
          placeholder='e.g. "Same-week lien starts; imaging on site"'
          maxLength={200}
          value={settings.referral_notes || ''}
          onChange={(e) => { setSaved(false); setSettings((s) => ({ ...s, referral_notes: e.target.value })); }}
        />
      </div>

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          className="px-5 py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50 flex items-center gap-1.5"
        >
          {saving ? (<><Loader2 size={13} className="animate-spin" /> Saving…</>)
            : saved ? (<><Check size={13} /> Saved</>)
            : 'Save referral preferences'}
        </button>
      </div>
    </div>
  );
}
